import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * SEIKAT Loading Screen
 * - Letter-by-letter reveal of "SEIKAT"
 * - Falling petal SVG particles (pink + lime)
 * - Custom progress 0 → 100
 * - Curtain wipe exit
 */
export default function LoadingScreen({ phrase = "Merangkai puisi alam", onComplete, duration = 2400 }) {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const elapsed = t - start;
      const p = Math.min(100, Math.round((elapsed / duration) * 100));
      setProgress(p);
      if (p < 100) {
        raf = requestAnimationFrame(tick);
      } else {
        // small hold before exit
        setTimeout(() => setDone(true), 350);
        // notify after curtain animation
        setTimeout(() => onComplete && onComplete(), 350 + 1150);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [duration, onComplete]);

  const letters = "SEIKAT".split("");

  // generate 18 petals with varied positions
  const petals = Array.from({ length: 18 }).map((_, i) => ({
    id: i,
    left: `${(i * 6.3) % 100}%`,
    delay: `${(i * 0.42) % 6}s`,
    dur: `${6 + (i % 5)}s`,
    drift: `${(i % 2 === 0 ? 1 : -1) * (30 + (i % 4) * 20)}px`,
    lime: i % 3 === 0
  }));

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="loading-screen"
          className="fixed inset-0 z-[100] bg-seikat-ink overflow-hidden grain"
          initial={{ y: 0 }}
          animate={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 1.1, ease: [0.86, 0, 0.07, 1] }}
        >
          {/* Petals */}
          <div className="absolute inset-0 pointer-events-none">
            {petals.map((p) => (
              <span
                key={p.id}
                className={`petal ${p.lime ? "petal--lime" : ""}`}
                style={{
                  left: p.left,
                  ["--delay"]: p.delay,
                  ["--dur"]: p.dur,
                  ["--drift"]: p.drift
                }}
              />
            ))}
          </div>

          {/* Center brand reveal */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="flex items-center gap-[0.05em] mb-6 select-none" data-testid="loading-brand">
              {letters.map((l, i) => (
                <span
                  key={i}
                  className="seikat-letter font-heading text-seikat-bone text-7xl sm:text-8xl lg:text-9xl tracking-tight"
                  style={{ animationDelay: `${i * 0.12}s` }}
                >
                  {l}
                </span>
              ))}
            </div>

            <motion.p
              className="font-body text-seikat-bone-muted text-sm tracking-[0.32em] uppercase mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              data-testid="loading-phrase"
            >
              {phrase}
            </motion.p>

            <motion.div
              className="flex items-baseline gap-2 font-body text-seikat-bone-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              data-testid="loading-progress"
            >
              <span className="text-5xl font-light text-seikat-bone tabular-nums">
                {String(progress).padStart(3, "0")}
              </span>
              <span className="text-xs tracking-[0.3em] uppercase">/100</span>
            </motion.div>
          </div>

          {/* Bottom progress line */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5">
            <motion.div
              className="h-full bg-gradient-to-r from-seikat-pink to-seikat-lime origin-left"
              style={{ width: `${progress}%` }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
